import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useProgram } from '../../services/programs';
import ProgramCreationWizard from './wizard/ProgramCreationWizard';
import type { ProgramWizardInitialData } from './wizard/useProgramWizardState';

export default function ProgramEditPage() {
  const { id: programId } = useParams<{ id: string }>();

  const { data: program, isLoading, error: fetchError } = useProgram(programId);

  // ─── Loading skeleton ──────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        <div className="h-10 w-64 rounded-xl bg-neutral-100 dark:bg-neutral-800 animate-pulse" />
        <div className="flex items-center gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-8 w-24 rounded-xl bg-neutral-100 dark:bg-neutral-800 animate-pulse" />
          ))}
        </div>
        <div className="h-[400px] rounded-2xl bg-neutral-100 dark:bg-neutral-800 animate-pulse" />
      </div>
    );
  }

  // ─── Error / not found ─────────────────────────────────────────────────────
  const errorMessage = fetchError
    ? fetchError.message?.includes('404')
      ? 'Program not found'
      : fetchError.message || 'Failed to load program'
    : null;

  if (errorMessage || !program || !programId) {
    return (
      <div className="min-h-screen bg-background-primary flex items-center justify-center">
        <div className="max-w-md mx-auto text-center px-4">
          <h2 className="text-2xl font-bold text-text-primary mb-2">
            {errorMessage === 'Program not found' ? 'Program Not Found' : 'Unable to Load Program'}
          </h2>
          <p className="text-text-secondary mb-6">
            {errorMessage ?? 'The requested program could not be loaded.'}
          </p>
          <Link
            to="/programs"
            className="inline-block px-5 py-2.5 text-sm font-bold bg-primary-500 rounded-full hover:bg-primary-600 dark:hover:bg-primary-400 transition-colors"
          >
            Back to Programs
          </Link>
        </div>
      </div>
    );
  }

  // ─── Build initial wizard data from existing program ───────────────────────
  const initialData: ProgramWizardInitialData = {
    programId: programId!,
    name: program.name,
    description: program.description,
    header_image: program.header_image,
    duration_days: program.duration_days,
    certification_type: program.certification_type,
    certificate_file_key: program.certificate_file_key,
  };

  // ─── Render wizard in edit mode ────────────────────────────────────────────
  return <ProgramCreationWizard initialData={initialData} />;
}
